<script lang="ts">
  import { store } from '$lib/store.svelte';
  import SignIn from './routes/SignIn.svelte';
  import Today from './routes/Today.svelte';
  import Reports from './routes/Reports.svelte';
  import Clients from './routes/Clients.svelte';
  import Import from './routes/Import.svelte';

  type Route = 'today' | 'reports' | 'clients' | 'import';
  const ROUTES: Route[] = ['today', 'reports', 'clients', 'import'];

  function readHash(): Route {
    const hash = location.hash.replace(/^#\/?/, '') as Route;
    return ROUTES.includes(hash) ? hash : 'today';
  }

  let route = $state<Route>(readHash());

  $effect(() => {
    const onHashChange = () => (route = readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  });

  // `store.init()` / `timer.init()` are called from main.ts, not from an effect
  // here — see the note there.
</script>

<div class="app">
  <header class="top">
    <a class="brand" href="#/today">
      Timesheet
      {#if store.syncing}<span class="sync" title="Syncing" aria-label="Syncing"></span>{/if}
      {#if !store.online}<span class="offline" title="Offline">offline</span>{/if}
    </a>

    <nav>
      {#each ROUTES as r (r)}
        <a href="#/{r}" class:active={route === r}>{r}</a>
      {/each}
    </nav>
  </header>

  {#if store.error}
    <div class="banner" role="status">
      <span>{store.error}</span>
      {#if !store.email}
        <a class="reconnect" href="/auth/login">Reconnect Google</a>
      {:else}
        <button class="ghost" onclick={() => (store.error = null)} aria-label="Dismiss">×</button>
      {/if}
    </div>
  {/if}

  <main>
    {#if store.status === 'loading'}
      <p class="muted center">Loading…</p>
    {:else if store.status === 'signed-out'}
      <SignIn />
    {:else if route === 'today'}
      <Today />
    {:else if route === 'reports'}
      <Reports />
    {:else if route === 'clients'}
      <Clients />
    {:else}
      <Import />
    {/if}
  </main>
</div>

<style>
  .app {
    max-width: 46rem;
    margin: 0 auto;
    padding: 0 1rem 4rem;
  }

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 0 0.85rem;
    position: sticky;
    top: 0;
    background: color-mix(in srgb, var(--bg) 92%, transparent);
    backdrop-filter: blur(8px);
    z-index: 10;
    border-bottom: 1px solid var(--line);
    margin-bottom: 1.25rem;
  }

  .brand {
    font-weight: 700;
    letter-spacing: -0.01em;
    text-decoration: none;
    color: var(--fg);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .sync {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    animation: blink 1.1s ease-in-out infinite;
  }

  @keyframes blink {
    50% {
      opacity: 0.2;
    }
  }

  .offline {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
  }

  nav {
    display: flex;
    gap: 0.15rem;
  }

  nav a {
    text-decoration: none;
    color: var(--muted);
    padding: 0.4rem 0.6rem;
    border-radius: 8px;
    font-size: 0.88rem;
    text-transform: capitalize;
  }

  nav a.active {
    color: var(--fg);
    background: var(--surface-2);
    font-weight: 650;
  }

  .banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    background: color-mix(in srgb, var(--danger) 14%, var(--surface));
    border: 1px solid color-mix(in srgb, var(--danger) 40%, var(--line));
    border-radius: var(--radius);
    padding: 0.6rem 0.85rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }

  .reconnect {
    color: var(--accent);
    font-weight: 650;
    white-space: nowrap;
  }

  .center {
    text-align: center;
    padding: 3rem 0;
  }
</style>
